import { createRoot } from 'react-dom/client';

/**
 * Ensures the Roboto font is loaded by adding the link to the document head
 * if it doesn't already exist
 */
function ensureRobotoFont(): void {
  // Check if Roboto font is already loaded
  const existingLink = Array.from(document.head.querySelectorAll('link')).find(
    (link) =>
      link.href.includes('fonts.googleapis.com') && link.href.includes('Roboto')
  );

  if (!existingLink) {
    // Create a new link element for the Roboto font
    const robotoLink = document.createElement('link');
    robotoLink.rel = 'stylesheet';
    robotoLink.href =
      'https://fonts.googleapis.com/css2?family=Roboto:ital,wght@0,100;0,300;0,400;0,500;0,700;0,900;1,100;1,300;1,400;1,500;1,700;1,900&display=swap';

    // Insert at the beginning of the head to ensure it loads as early as possible
    document.head.insertBefore(robotoLink, document.head.firstChild);
  }
}

/**
 * Creates a shadow root with the specified styles and returns a React root in it.
 * @param {string} styles - CSS styles to be applied to the shadow root.
 * @returns {ReactRoot} - React root rendered inside the shadow root.
 */
export default function createShadowRoot(styles: string) {
  // Ensure Roboto font is loaded
  ensureRobotoFont();

  // Check if container already exists
  const existingContainer = document.getElementById('root-atarim');
  if (existingContainer) {
    console.warn(
      'Atarim root already exists, avoiding duplicate initialization'
    );
    // Return the existing React root if possible
    // Note: This is a simplification, as we can't truly retrieve an existing React root
    // In a real implementation, you might want to manage roots differently
    return null;
  }

  // Create a container element to hold the shadow root
  const container = document.createElement('div');
  container.id = 'root-atarim';
  container.style.pointerEvents = 'none';
  container.setAttribute('data-capture', 'exclude');

  // Attach a shadow root to the container element
  const shadow = container.attachShadow({ mode: 'open' });

  // Add a minimal reset that won't interfere with Tailwind
  // Including explicit rem size protection
  const resetStyles = `
  :host {
    all: initial;
  }
  
  #app-wrapper {
    position: absolute;
    z-index: 2147483647;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    isolation: isolate;
    text-align: left;
    font-size: 16px;
    font-weight: 400;
    font-family: 'Roboto', sans-serif;
    font-variation-settings: normal;
    text-transform: none;
    text-shadow: none;
  }
`;

  // Combine reset styles with provided styles
  const combinedStyles = resetStyles + styles;

  // Create a new CSS style sheet and apply the combined styles
  const styleSheet = new CSSStyleSheet();
  styleSheet.replaceSync(combinedStyles);

  // Apply the style sheet to the shadow root
  shadow.adoptedStyleSheets = [styleSheet];

  // Create and style the appWrapper inside the shadow root
  const appWrapper = document.createElement('div');
  appWrapper.id = 'app-wrapper';
  appWrapper.setAttribute('data-capture', 'exclude');
  shadow.appendChild(appWrapper);

  // Append the container element to the document body
  document.body.appendChild(container);

  // Return a React root created for the appWrapper inside the shadow root
  return createRoot(appWrapper);
}
